import React, { useState, useEffect } from 'react';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { useSearchParams } from 'react-router-dom';
import { FadeLoader } from 'react-spinners';
import { useAnalytics } from '../hooks/analytics/useAnalytics';
import { SCREEN_NAMES } from '../constants/analytics';
import { queryRequest } from '../services/internalApi';

const REQUEST_TIMEOUT_MS = 60000;

const withTimeout = (promise, ms) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), ms)),
]);

export function PrivilegedMemberHelpfulTools() {
    const [loading, setLoading] = useState(true);
    const [privilegedMemberHelpfulToolsList, setPrivilegedMemberHelpfulToolsList] = useState([]);
    const [searchParams] = useSearchParams();
    const userToken = searchParams.get('user_token') || null;

    // ページ表示情報をアナリティクスに送信する
    const analytics = useAnalytics();
    const toolType = {
        richText: 1,
        externalUrl: 2,
    };

    useEffect(() => {
        const fetchPrivilegedMemberHelpfulToolsList = async () => {
            setLoading(true);

            try {
                const queryString = `
                    query ${userToken ? '($userToken: String!)' : ''} {
                        getPrivilegedMemberHelpfulToolsList {
                            common {
                                statusCode
                                message
                            }
                            helpfulToolsList {
                                toolTitle
                                toolType
                                toolId
                                toolURL
                                toolCautionText
                            }
                        }
                        ${userToken ? `
                            getMemberIdFromUserToken(userToken: $userToken) {
                                common {
                                    statusCode
                                    message
                                }
                                memberId
                            }
                        ` : ''}
                    }
                `;
                const result = await withTimeout(
                    queryRequest(queryString, userToken ? { variables: { userToken } } : {}),
                    REQUEST_TIMEOUT_MS
                );

                const { common, helpfulToolsList } = result.data.getPrivilegedMemberHelpfulToolsList;
                if (common.statusCode === 200) {
                    setPrivilegedMemberHelpfulToolsList(helpfulToolsList ?? []);
                } else {
                    console.error('Error fetching privileged member helpful tools list:', common.message);
                }

                const fetchedMemberId = result.data.getMemberIdFromUserToken?.memberId ?? null;

                if(fetchedMemberId !== null) {
                    // ユーザーIDを設定(既に同じIDが設定されている場合はスキップされる)
                    analytics.setAnalyticsUserId(fetchedMemberId);
                    // データ取得完了後にアナリティクスを送信
                    analytics.trackScreenView(SCREEN_NAMES.PRIVILEGED_MEMBER_HELPFUL_TOOLS);
                }
            } catch (err) {
                console.error('Error fetching privileged member helpful tools list:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPrivilegedMemberHelpfulToolsList();
        // マウント時に一度だけ実行する意図的な設計（analyticsはuseMemoで参照が安定しているため省略可）
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buildUrlWithToken = (url) => {
        if (!userToken) return url;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}user_token=${userToken}`;
    };

    if (loading) {
        return (
            <div className="App-loading">
                <FadeLoader
                    color="#78D7FF"
                    loading={loading}
                    size={100}
                />
            </div>
        );
    }

    return (
        <>
            <ul className="tool-list">
                {privilegedMemberHelpfulToolsList.length > 0 ? (
                    privilegedMemberHelpfulToolsList.map((privilegedMemberHelpfulTools, index) => (
                        <li key={index}>
                            <div className="tool-item">
                                <a href={privilegedMemberHelpfulTools.toolType === toolType.richText ? buildUrlWithToken(`/privileged-member-helpful-tools/${privilegedMemberHelpfulTools.toolId}`) : privilegedMemberHelpfulTools.toolURL} className="tool-button">
                                    {privilegedMemberHelpfulTools.toolTitle}
                                </a>
                                {privilegedMemberHelpfulTools.toolCautionText != null && privilegedMemberHelpfulTools.toolCautionText.length > 0 ?
                                    <div className="tool-description">
                                        {privilegedMemberHelpfulTools.toolCautionText}
                                    </div>
                                    : (
                                        <>
                                        </>
                                    )
                                }
                            </div>
                        </li>
                    ))
                )
                    : (
                        <>
                        </>
                    )
                }
            </ul>
            <div className="privileged-member-legal-link-container">
                <a href={buildUrlWithToken('/privileged-member-terms-of-service')} className="privileged-member-legal-link">
                    利用規約
                </a>
                <a href={buildUrlWithToken('/privileged-member-privacy-policy')} className="privileged-member-legal-link">
                    プライバシーポリシー
                </a>
            </div>
        </>
    );
}
